import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Stack,
  Typography,
  Paper,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Pagination,
  Card,
  CardContent,
  LinearProgress,
  Divider,
  Alert,
} from '@mui/material';
import { fetchSurveyById, fetchResponseSummary, fetchResponses } from '../controllers/surveysController';
import BackButton from '../components/BackButton';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ListIcon from '@mui/icons-material/List';

const QuestionTypeLabels = {
  0: 'Tek Seçim',
  1: 'Çoklu Seçim',
  2: 'Kısa Metin',
  3: 'Uzun Metin',
  4: 'Değerlendirme (1-5)',
  5: 'Tarih',
  6: 'Sayı',
};

const ParticipantTypeLabels = {
  0: 'Üye',
  1: 'Alt Üye',
};

// Helper function to format UTC date without timezone conversion
const formatUTCDateTime = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  return `${day}.${month}.${year} ${hours}:${minutes}`;
};

const SurveyResponses = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [survey, setSurvey] = useState(null);
  const [summary, setSummary] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [responsesLoading, setResponsesLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [responsesPage, setResponsesPage] = useState(1);
  const [pageSize] = useState(10);
  const [responsesTotalPages, setResponsesTotalPages] = useState(1);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const [surveyData, summaryData] = await Promise.all([
          fetchSurveyById(id),
          fetchResponseSummary(id).catch(() => null),
        ]);
        if (!active) return;
        setSurvey(surveyData);
        setSummary(summaryData);
      } catch (e) {
        if (!active) return;
        setError(e?.message || 'Anket bilgileri alınamadı');
      } finally {
        if (active) {
          setLoading(false);
          setSummaryLoading(false);
        }
      }
    })();
    return () => { active = false; };
  }, [id]);

  useEffect(() => {
    if (activeTab !== 1) return;
    let active = true;
    (async () => {
      setResponsesLoading(true);
      try {
        const { data, meta } = await fetchResponses(id, { pageNumber: responsesPage, pageSize });
        if (!active) return;
        setResponses(data || []);
        setResponsesTotalPages(meta?.totalPages || 1);
      } catch (e) {
        if (!active) return;
        console.error('Cevaplar alınamadı:', e);
      } finally {
        if (active) setResponsesLoading(false);
      }
    })();
    return () => { active = false; };
  }, [id, activeTab, responsesPage, pageSize]);

  const renderQuestionSummary = (questionSummary) => {
    const { questionType, totalAnswers, skippedCount, optionSummaries, ratingSummary, numberSummary, dateSummary, sampleTextAnswers, totalTextAnswers } = questionSummary;

    // Find the question from survey to get its options
    const questionFromSurvey = survey?.questions?.find(q => q.id === questionSummary.questionId);
    const questionOptions = questionFromSurvey?.options || [];

    // Use optionSummaries if available, otherwise use question options
    const optionsToDisplay = (optionSummaries && optionSummaries.length > 0) 
      ? optionSummaries 
      : questionOptions.map((opt, idx) => ({
          optionId: opt.id,
          optionText: opt.optionText,
          selectionCount: 0,
          percentage: 0,
        }));

    return (
      <Card key={questionSummary.questionId} sx={{ border: '1px solid #e7e7e7', boxShadow: 'none', borderRadius: '8px', mb: 2 }}>
        <CardContent sx={{ p: 2.5 }}>
          <Stack spacing={2.5}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#444444' }}>
                {questionSummary.questionText}
              </Typography>
              <Chip 
                label={QuestionTypeLabels[questionType] || 'Bilinmeyen'} 
                size="small" 
                sx={{ height: 24, fontSize: '11px', fontWeight: 500, backgroundColor: '#E7EDF4', color: '#0E4D94' }} 
              />
              <Chip 
                label={`${totalAnswers} Cevap`} 
                size="small" 
                sx={{ height: 24, fontSize: '11px', fontWeight: 500, backgroundColor: '#E7EDF4', color: '#0E4D94' }} 
              />
              {skippedCount > 0 && (
                <Chip 
                  label={`${skippedCount} Atlanmış`} 
                  size="small" 
                  sx={{ height: 24, fontSize: '11px', fontWeight: 500, backgroundColor: '#FFF4E6', color: '#FF9800' }} 
                />
              )}
            </Stack>

            {questionType === 0 || questionType === 1 ? (
              // Single Choice or Multiple Choice
              optionsToDisplay && optionsToDisplay.length > 0 ? (
                <Box sx={{ pt: 1.5, borderTop: '1px solid #e6e6e6' }}>
                  <Stack spacing={1.5}>
                    {optionsToDisplay.map((option, idx) => (
                      <Box 
                        key={option.optionId || `option-${idx}`}
                        sx={{ 
                          backgroundColor: '#fafafa', 
                          borderRadius: '8px', 
                          p: 1.5, 
                          border: '1px solid #e6e6e6',
                          transition: 'all 0.2s',
                          '&:hover': {
                            borderColor: '#0E4D94',
                            backgroundColor: '#ffffff',
                          },
                        }}
                      >
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                          <Chip
                            label={idx + 1}
                            size="small"
                            sx={{
                              width: 28,
                              height: 28,
                              fontSize: '11px',
                              fontWeight: 600,
                              backgroundColor: '#0E4D94',
                              color: '#ffffff',
                              flexShrink: 0,
                            }}
                          />
                          <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#444444', flex: 1 }}>
                            {option.optionText}
                          </Typography>
                          {option.selectionCount !== undefined && option.percentage !== undefined ? (
                            <Chip
                              label={`${option.selectionCount} (${option.percentage.toFixed(1)}%)`}
                              size="small"
                              sx={{
                                height: 24,
                                fontSize: '11px',
                                fontWeight: 600,
                                backgroundColor: '#E7EDF4',
                                color: '#0E4D94',
                                flexShrink: 0,
                              }}
                            />
                          ) : (
                            <Chip
                              label="Henüz cevap yok"
                              size="small"
                              sx={{
                                height: 24,
                                fontSize: '11px',
                                fontWeight: 500,
                                backgroundColor: '#fafafa',
                                color: '#a3a3a3',
                                flexShrink: 0,
                              }}
                            />
                          )}
                        </Stack>
                        {option.percentage !== undefined && option.percentage > 0 && (
                          <LinearProgress 
                            variant="determinate" 
                            value={option.percentage} 
                            sx={{ 
                              height: 10, 
                              borderRadius: '5px',
                              backgroundColor: '#E7EDF4',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: '#0E4D94',
                                borderRadius: '5px',
                              },
                            }} 
                          />
                        )}
                      </Box>
                    ))}
                  </Stack>
                </Box>
              ) : (
                <Typography sx={{ fontSize: '14px', color: '#666666' }}>Bu soru için seçenek bulunmamaktadır.</Typography>
              )
            ) : questionType === 4 ? (
              // Rating
              ratingSummary && (
                <Stack spacing={2.5}>
                  <Box>
                    <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>Ortalama Değerlendirme</Typography>
                    <Typography sx={{ fontSize: '20px', fontWeight: 600, color: '#444444' }}>
                      {ratingSummary.averageRating.toFixed(2)} / 5
                    </Typography>
                  </Box>
                  <Divider sx={{ borderColor: '#e6e6e6' }} />
                  <Stack spacing={1.5}>
                    {[1, 2, 3, 4, 5].map((rating) => {
                      const count = ratingSummary.ratingDistribution[rating] || 0;
                      const percentage = ratingSummary.ratingPercentages[rating] || 0;
                      return (
                        <Box key={rating}>
                          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                            <Typography sx={{ fontSize: '14px', color: '#444444' }}>{rating} Yıldız</Typography>
                            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#444444' }}>
                              {count} ({percentage.toFixed(1)}%)
                            </Typography>
                          </Stack>
                          <LinearProgress 
                            variant="determinate" 
                            value={percentage} 
                            sx={{ 
                              height: 8, 
                              borderRadius: '4px',
                              backgroundColor: '#E7EDF4',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: '#0E4D94',
                              },
                            }} 
                          />
                        </Box>
                      );
                    })}
                  </Stack>
                </Stack>
              )
            ) : questionType === 6 ? (
              // Number
              numberSummary && (
                <Stack direction="row" spacing={3} flexWrap="wrap">
                  <Box>
                    <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>Ortalama</Typography>
                    <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#444444' }}>
                      {numberSummary.average.toFixed(2)}
                    </Typography>
                  </Box>
                  {numberSummary.min !== null && numberSummary.min !== undefined && (
                    <Box>
                      <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>Minimum</Typography>
                      <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#444444' }}>
                        {numberSummary.min}
                      </Typography>
                    </Box>
                  )}
                  {numberSummary.max !== null && numberSummary.max !== undefined && (
                    <Box>
                      <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>Maksimum</Typography>
                      <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#444444' }}>
                        {numberSummary.max}
                      </Typography>
                    </Box>
                  )}
                  {numberSummary.median !== null && numberSummary.median !== undefined && (
                    <Box>
                      <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>Medyan</Typography>
                      <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#444444' }}>
                        {numberSummary.median.toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              )
            ) : questionType === 5 ? (
              // Date
              dateSummary && (
                <Stack spacing={2.5}>
                  {dateSummary.earliestDate && (
                    <Box>
                      <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>En Erken Tarih</Typography>
                      <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#444444' }}>
                        {formatUTCDateTime(dateSummary.earliestDate)}
                      </Typography>
                    </Box>
                  )}
                  {dateSummary.latestDate && (
                    <Box>
                      <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 0.5 }}>En Geç Tarih</Typography>
                      <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#444444' }}>
                        {formatUTCDateTime(dateSummary.latestDate)}
                      </Typography>
                    </Box>
                  )}
                  {Object.keys(dateSummary.dateDistribution).length > 0 && (
                    <Box>
                      <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 1 }}>Tarih Dağılımı</Typography>
                      <Stack spacing={0.5}>
                        {Object.entries(dateSummary.dateDistribution)
                          .sort((a, b) => a[0].localeCompare(b[0]))
                          .map(([date, count]) => (
                            <Stack key={date} direction="row" alignItems="center" justifyContent="space-between">
                              <Typography sx={{ fontSize: '14px', color: '#444444' }}>{date}</Typography>
                              <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#444444' }}>
                                {count}
                              </Typography>
                            </Stack>
                          ))}
                      </Stack>
                    </Box>
                  )}
                </Stack>
              )
            ) : (
              // Text or LongText
              <Stack spacing={1.5}>
                <Typography sx={{ fontSize: '14px', color: '#666666' }}>
                  Toplam {totalTextAnswers} metin cevabı
                </Typography>
                {sampleTextAnswers && sampleTextAnswers.length > 0 && (
                  <Box>
                    <Typography sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 1 }}>Örnek Cevaplar:</Typography>
                    <Stack spacing={1.5}>
                      {sampleTextAnswers.map((answer, idx) => (
                        <Card key={idx} sx={{ border: '1px solid #e6e6e6', boxShadow: 'none', borderRadius: '8px', p: 1.5, backgroundColor: '#fafafa' }}>
                          <Typography sx={{ fontSize: '14px', color: '#444444', whiteSpace: 'pre-wrap' }}>
                            {answer}
                          </Typography>
                        </Card>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Stack>
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <BackButton onClick={() => navigate('/surveys')} label="Anketlere Dön" />
      </Box>
    );
  }

  if (!survey) return null;

  return (
    <Stack spacing={3}>
      <Stack spacing={2} sx={{ mb: 2 }}>
        <BackButton onClick={() => navigate(`/surveys/${id}`)} />
      </Stack>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography sx={{ fontSize: '18px', fontWeight: 500, color: '#444444' }}>
          {survey.title} - Cevaplar
        </Typography>
      </Stack>

      {summaryLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : summary ? (
        <Card sx={{ border: '1px solid #e7e7e7', boxShadow: 'none', borderRadius: '8px', p: 2.5 }}>
          <Stack spacing={2.5}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <AssessmentIcon sx={{ fontSize: '20px', color: '#0E4D94' }} />
              <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#444444' }}>
                Özet İstatistikler
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
              <Chip 
                label={`Toplam ${summary.totalResponses} Cevap`} 
                sx={{ height: 24, fontSize: '11px', fontWeight: 500, backgroundColor: '#E7EDF4', color: '#0E4D94' }} 
              />
              <Chip 
                label={`${summary.questionSummaries?.length || 0} Soru`} 
                sx={{ height: 24, fontSize: '11px', fontWeight: 500, backgroundColor: '#E7EDF4', color: '#0E4D94' }} 
              />
            </Stack>
          </Stack>
        </Card>
      ) : (
        <Alert severity="info" sx={{ fontSize: '14px' }}>Henüz cevap bulunmamaktadır.</Alert>
      )}

      <Card sx={{ border: '1px solid #e7e7e7', boxShadow: 'none', borderRadius: '8px', overflow: 'hidden' }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, v) => setActiveTab(v)}
          sx={{
            borderBottom: '1px solid #e6e6e6',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              minHeight: 48,
              '&.Mui-selected': {
                color: '#0E4D94',
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#0E4D94',
            },
          }}
        >
          <Tab icon={<AssessmentIcon sx={{ fontSize: '18px' }} />} iconPosition="start" label="Özet" />
          <Tab icon={<ListIcon sx={{ fontSize: '18px' }} />} iconPosition="start" label="Cevaplar" />
        </Tabs>

        <Box sx={{ p: 2.5 }}>
          {activeTab === 0 && (
            <Box>
              {summaryLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : summary && summary.questionSummaries && summary.questionSummaries.length > 0 ? (
                <Stack spacing={2}>
                  {summary.questionSummaries.map((qs) => renderQuestionSummary(qs))}
                </Stack>
              ) : (
                <Alert severity="info" sx={{ fontSize: '14px' }}>Henüz soru özeti bulunmamaktadır.</Alert>
              )}
            </Box>
          )}

          {activeTab === 1 && (
            <Box>
              {responsesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : responses.length > 0 ? (
                <Stack spacing={2}>
                  <TableContainer
                    sx={{
                      boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
                      border: '1px solid #f5f5f5',
                      borderRadius: '8px',
                      overflow: 'hidden',
                    }}
                  >
                    <Table size="small">
                      <TableHead sx={{ bgcolor: 'transparent' }}>
                        <TableRow>
                          <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Katılımcı</TableCell>
                          <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Katılımcı Tipi</TableCell>
                          <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Gönderilme Tarihi</TableCell>
                          <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Güncellenme Tarihi</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {responses.map((response, index) => (
                          <TableRow
                            key={response.id}
                            hover
                            sx={{ 
                              cursor: 'pointer',
                              '& .MuiTableCell-root': {
                                borderBottom: index === responses.length - 1 ? 'none' : '1px dashed #e8e8e8',
                                px: 1.5,
                                py: 1.5,
                                color: '#444444',
                                fontWeight: 500,
                              },
                              '&:hover': {
                                backgroundColor: '#fafafa',
                              },
                            }}
                            onClick={() => navigate(`/surveys/${id}/participant-response?personId=${response.personId}&participantType=${response.participantType}`)}
                          >
                            <TableCell>{response.fullName || '-'}</TableCell>
                            <TableCell>
                              <Chip
                                label={ParticipantTypeLabels[response.participantType] || 'Bilinmeyen'}
                                size="small"
                                sx={{ height: 24, fontSize: '11px', fontWeight: 500, backgroundColor: '#E7EDF4', color: '#0E4D94' }}
                              />
                            </TableCell>
                            <TableCell>{formatUTCDateTime(response.submittedAtUtc)}</TableCell>
                            <TableCell>{response.updatedAtUtc ? formatUTCDateTime(response.updatedAtUtc) : '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Pagination
                      count={responsesTotalPages}
                      page={responsesPage}
                      onChange={(e, v) => setResponsesPage(v)}
                      color="primary"
                    />
                  </Box>
                </Stack>
              ) : (
                <Alert severity="info" sx={{ fontSize: '14px' }}>Henüz cevap bulunmamaktadır.</Alert>
              )}
            </Box>
          )}

        </Box>
      </Card>
    </Stack>
  );
};

export default SurveyResponses;

